import { action, computed, makeObservable, observable } from 'mobx'

import { ISharedSitePermit } from '~/client/graph'
import ProjectDateStore, {
  areIntervalTimesIntersects,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import ILogisticItem, { LogisticItemApp } from '../../models/ILogisticItem'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Workflow from '../../models/Workflow'
import WorkflowType from '../../models/WorkflowType'
import ClosuresStore from '../domain/Closures.store'
import CompaniesStore from '../domain/Companies.store'
import GlobeViewsStore from '../domain/GlobeViews.store'
import LocationAttributesStore from '../domain/LocationAttributes.store'
import SharedSitePermitStore from '../domain/SharedSitePermit.store'
import SitemapsStore from '../domain/Sitemaps.store'
import WorkflowCategoriesStore from '../domain/WorkflowCategories.store'
import WorkflowTypesStore from '../domain/WorkflowTypes.store'
import EventsStore from '../EventStore/Events.store'
import { IFilters } from '../InitialState'

export default abstract class BaseLogisticsStore {
  public constructor(
    protected readonly projectDateStore: ProjectDateStore,
    protected readonly eventsStore: EventsStore,
    protected readonly sitemapsStore: SitemapsStore,
    protected readonly globeViewsStore: GlobeViewsStore,
    protected readonly permitTypesStore: WorkflowTypesStore,
    protected readonly formCategoriesStore: WorkflowCategoriesStore,
    protected readonly locationAttributesStore: LocationAttributesStore,
    protected readonly companiesStore: CompaniesStore,
    protected readonly closuresStore: ClosuresStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
  ) {
    makeObservable(this)
  }

  @observable public linkedWorkflowToPreview: Workflow | null = null

  @action.bound
  public previewLinkedWorkflow(permit: Workflow): void {
    this.linkedWorkflowToPreview =
      this.allForms.find(f => f.id === permit.id)?.copy() || permit.copy()
  }

  @action.bound
  public closeLinkedWorkflowPreview(): void {
    this.linkedWorkflowToPreview = null
  }

  public abstract get filters(): IFilters

  @computed
  public get isMapViewDisabled(): boolean {
    // until we receive all the data, we cannot switch the default view mode
    if (
      !this.sitemapsStore.isDataReceived ||
      !this.globeViewsStore.isDataReceived
    ) {
      return false
    }

    if (!this.state.workflows.isDataReceived) {
      return false
    }

    return !this.state.isThereFormsAssociatedMap
  }

  @action.bound
  public resetSelectedSharedEntity(): void {
    return
  }

  @action.bound
  // eslint-disable-next-line
  public setSelectedSharedEntity(entity: ISharedSitePermit): void {
    return
  }

  @computed
  public get allLogistics(): ILogisticItem[] {
    return this.convertEntitiesToLogistics(this.allForms, this.allSharedForms)
  }

  public convertSharedFormsToLogistics = (
    sharedForms: ISharedSitePermit[],
  ): ILogisticItem[] => {
    return sharedForms
      .map(form => ({
        sharedForm: form,
        form: Workflow.fromSharedPermitDto(form),
      }))
      .map<ILogisticItem>(({ sharedForm, form }) => ({
        id: form.id,
        entityId: form.id,
        app: LogisticItemApp.FORM,
        name: form.getCaption(this.permitTypesStore),
        shortName: form.getCaption(this.permitTypesStore, true),
        code: form.code,
        locations: this.getLocationsFromIds(form.locations.map(l => l.id)),
        equipment: this.getLocationsFromIds(form.equipment.map(l => l.id)),
        status: form.status,
        isStatusLate: form.isLate,
        workflowStepLevel: form.workflowStepLevel,
        companyIds: form.companyIds || [],
        startDate: form.startDate,
        type: form.getTypeOfPermitType(this.permitTypesStore),
        endDate: form.endDate,
        responsibleContactIds: form.requesterIds || [],
        attachments: form.attachments,
        projectId: sharedForm.projectFromId,
        original: form,
        isShared: true,
      }))
  }

  public convertFormsToLogistics = (forms: Workflow[]): ILogisticItem[] => {
    return forms.map<ILogisticItem>(form => ({
      id: form.id,
      entityId: form.id,
      app: LogisticItemApp.FORM,
      name: form.getCaption(this.permitTypesStore),
      shortName: form.getCaption(this.permitTypesStore, true),
      code: form.code,
      locations: this.getLocationsFromIds(form.locations.map(l => l.id)),
      equipment: this.getLocationsFromIds(form.equipment.map(l => l.id)),
      status: form.status,
      isStatusLate: form.isLate,
      workflowStepLevel: form.workflowStepLevel,
      companyIds: form.companyIds || [],
      startDate: form.startDate,
      type: form.getTypeOfPermitType(this.permitTypesStore),
      endDate: form.endDate,
      attachments: form.attachments,
      responsibleContactIds: form.responsibleIds || [],
      formCategoryId: form.categoryId,
      initialType: form.initialType,
      original: form,
      projectId: form.projectId,
    }))
  }

  public convertEntitiesToLogistics = (
    forms: Workflow[],
    sharedForms: ISharedSitePermit[] = [],
  ): ILogisticItem[] => {
    const _forms = this.convertFormsToLogistics(
      forms.filter(form =>
        this.formCategoriesStore.isCategoryFlagEnabled(form.categoryId),
      ),
    )
    const _sharedForms = this.convertSharedFormsToLogistics(sharedForms)

    return [..._forms, ..._sharedForms]
  }

  @computed
  public get logisticItemsInPeriodInterval(): ILogisticItem[] {
    return this.convertEntitiesToLogistics(
      this.formsInPeriodInterval,
      this.sharedFormsInPeriodInterval,
    )
  }

  @computed
  public get allSharedForms(): ISharedSitePermit[] {
    return this.sharedSitePermitStore.list
  }

  public abstract get allForms(): Workflow[]

  @computed
  public get formsByCreationDate(): Workflow[] {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.startDate)
    const endDate = endOfDay(this.endDate)

    return this.allForms
      .filter(form => form.isScheduledWithinRange(startDate, endDate))
      .sort((f1, f2) => f2.createdAt - f1.createdAt)
  }

  @computed
  public get formsInPeriodInterval(): Workflow[] {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.startDate)
    const endDate = endOfDay(this.endDate)

    return this.allForms.filter(form =>
      form.isScheduledWithinRange(startDate, endDate),
    )
  }

  @computed
  public get sharedFormsInPeriodInterval(): ISharedSitePermit[] {
    const { startOfDay, endOfDay } = this.projectDateStore
    const startDate = startOfDay(this.startDate)
    const endDate = endOfDay(this.endDate)

    return this.allSharedForms.filter(form =>
      areIntervalTimesIntersects(
        { startDate: new Date(startDate), endDate: new Date(endDate) },
        {
          startDate: new Date(form.startDate),
          endDate: new Date(form.endDate),
        },
      ),
    )
  }

  public getFormById = (id: string): Workflow | undefined => {
    return this.allForms.find(f => f.id === id)
  }

  public getSharedFormById = (id: string): ISharedSitePermit => {
    return this.sharedSitePermitStore.getById(id)
  }

  public get startDate(): Date {
    return new Date()
  }

  public get endDate(): Date {
    return new Date()
  }

  public get state() {
    return this.eventsStore.appState
  }

  public get isCreationAvailable(): boolean {
    const { isWorkflowsDisabled, userActiveProjectSettings } =
      this.eventsStore.appState

    return !isWorkflowsDisabled || userActiveProjectSettings?.isAdmin
  }

  protected getLocationById = (id: string): LocationBase => {
    return this.locationAttributesStore?.getById(id)
  }

  protected getLocationsFromIds = (ids: string[]): LocationBase[] => {
    return (
      ids.reduce((list, id) => {
        const loc = this.getLocationById(id)
        if (loc) {
          list.push(loc)
        }
        return list
      }, []) || []
    )
  }

  public getFormNameToDisplay = (formType: WorkflowType): string => {
    if (!formType) return null

    return `${
      formType.isDeleted
        ? `[${Localization.translator.deleted.toUpperCase()}] `
        : !formType.isEnabled
          ? `[${Localization.translator.disabled.toUpperCase()}] `
          : ''
    }${formType.name}`
  }
}
